import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Drawer from '../ui/Drawer';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import BrandLogo from '../ui/BrandLogo';
import ConfirmDialog from '../ui/ConfirmDialog';
import FieldError from '../ui/FieldError';
import { ErrorState } from '../ui/States';
import { ApiError } from '../../api/client';
import { couponsApi } from '../../api/coupons';
import CouponPreview from './CouponPreview';
import { PackageStatusBadge } from './PackageStatus';
import { ACTIVE_LOCK_HINT, describeTime, getTier, httpImageUrl, levelCount, levelStyle, packageTitle, STATUS_META } from './couponAdapter';
import { emptyForm, fieldId, firstErrorId, hasErrors, isDirty, LIMITS, mapServerErrors, toForm, toPayload, validateForm } from './couponForm';

const QUICK_DAYS = [7, 14, 30, 90];
const STATUS_OPTIONS = ['draft', 'active', 'archived'];

const inputBase =
  'w-full px-3 py-2 rounded-lg text-[13px] text-on-surface border outline-none transition placeholder:text-on-surface-variant/50';
const inputEditable = 'bg-surface-container-low border-outline-variant/30 focus:bg-surface-container-lowest focus:border-primary-container focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)]';
const inputInvalid = 'bg-status-alert-bg/40 border-error focus:shadow-[0_0_0_3px_rgba(186,26,26,0.15)]';
const inputReadOnly = 'bg-surface-container-low/60 border-transparent cursor-default';

const chipClass = (selected) =>
  `h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-colors outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot disabled:opacity-50 disabled:cursor-not-allowed ${
    selected ? 'bg-primary text-on-primary border-primary' : 'bg-surface-container-lowest text-on-surface-variant border-outline-variant/50 hover:bg-surface-container'
  }`;

function Label({ htmlFor, children, required, count }) {
  return (
    <div className="flex items-baseline justify-between gap-2 mb-1">
      <label htmlFor={htmlFor} className="text-xs font-bold text-on-surface">
        {children}
        {required && (
          <span className="text-error" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>
      {count && <span className="font-data-mono text-[10px] text-on-surface-variant">{count}</span>}
    </div>
  );
}

function StatusPicker({ value, lockedActive, readOnly, error, onChange }) {
  return (
    <div>
      <span id="coupon-status-label" className="block text-xs font-bold text-on-surface mb-1">
        Status
      </span>
      <div
        id="coupon-status"
        role="radiogroup"
        aria-labelledby="coupon-status-label"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? 'coupon-status-error' : undefined}
        tabIndex={-1}
        className="inline-flex p-1 rounded-lg bg-surface-container-low border border-outline-variant/30 outline-none"
      >
        {STATUS_OPTIONS.map((status) => {
          const selected = value === status;
          const locked = lockedActive && status !== 'active';
          return (
            // Disabled buttons don't show tooltips, so the hint goes on a wrapper
            <span key={status} title={locked ? ACTIVE_LOCK_HINT : undefined} className="inline-flex">
              <button
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={readOnly || locked}
                onClick={() => onChange(status)}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot disabled:cursor-not-allowed ${
                  selected ? 'bg-surface-container-lowest text-primary shadow-xs' : 'text-on-surface-variant hover:text-on-surface disabled:opacity-40'
                }`}
              >
                {STATUS_META[status].label}
              </button>
            </span>
          );
        })}
      </div>
      <FieldError id={error ? 'coupon-status-error' : undefined}>{error}</FieldError>
    </div>
  );
}

function LevelSection({ index, value, errors = {}, tier, readOnly, brandListId, onChange }) {
  const level = index + 1;
  const style = levelStyle(level);
  const [logoBroken, setLogoBroken] = useState(false);
  const logoUrl = httpImageUrl(value.logoUrl);
  useEffect(() => setLogoBroken(false), [logoUrl]);

  const input = (field) => `${inputBase} ${readOnly ? inputReadOnly : errors[field] ? inputInvalid : inputEditable}`;
  const describedBy = (field, extra) => [errors[field] ? `${fieldId(index, field)}-error` : null, extra].filter(Boolean).join(' ') || undefined;
  const common = (field, extra) => ({
    id: fieldId(index, field),
    value: value[field],
    readOnly,
    onChange: (e) => onChange(index, field, e.target.value),
    'aria-invalid': Boolean(errors[field]),
    'aria-describedby': describedBy(field, extra),
  });
  const counter = (field) => (readOnly ? null : `${value[field].length}/${LIMITS[field]}`);
  const days = String(value.validForDays).trim();

  return (
    <section
      aria-labelledby={`coupon-level-${level}-title`}
      className="bg-surface-container-lowest rounded-xl border border-surface-container p-4 shadow-sm flex flex-col gap-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-surface-container">
        <div className="flex items-center gap-2">
          <span className={`w-7 h-7 rounded-lg ${style.badge} text-on-primary font-data-mono font-bold text-xs flex items-center justify-center`}>
            L{level}
          </span>
          <h3 id={`coupon-level-${level}-title`} className="text-base font-bold text-on-surface">
            Level {level} Reward Tier
            {tier.scansRequired != null && (
              <span className="ml-2 text-[11px] font-normal text-on-surface-variant font-data-mono">
                (Scan threshold: {tier.scansRequired} scans)
              </span>
            )}
          </h3>
        </div>
        {tier.name && <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${style.chip}`}>{tier.name}</span>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
        <div>
          <Label htmlFor={fieldId(index, 'brandName')} required={!readOnly} count={counter('brandName')}>
            Brand name
          </Label>
          <input
            type="text"
            autoComplete="off"
            list={readOnly ? undefined : brandListId}
            maxLength={LIMITS.brandName}
            placeholder="e.g. HEMA"
            className={`${input('brandName')} font-semibold`}
            {...common('brandName')}
          />
          <FieldError id={errors.brandName ? `${fieldId(index, 'brandName')}-error` : undefined}>{errors.brandName}</FieldError>
        </div>
        <div>
          <Label htmlFor={`coupon-l${level}-scans`}>Scan requirement</Label>
          <div className="flex items-center gap-2">
            <input
              id={`coupon-l${level}-scans`}
              type="text"
              readOnly
              value={tier.scansRequired ?? '—'}
              className={`${inputBase} ${inputReadOnly} w-20 font-data-mono font-bold`}
              aria-describedby={`coupon-l${level}-scans-hint`}
            />
            <span id={`coupon-l${level}-scans-hint`} className="text-xs text-on-surface-variant whitespace-nowrap">
              Scans to unlock
            </span>
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor={fieldId(index, 'logoUrl')}>Logo URL</Label>
        <div className="flex items-center gap-2">
          <input
            type="url"
            inputMode="url"
            autoComplete="off"
            maxLength={LIMITS.logoUrl}
            placeholder="https://…/logo.png (optional)"
            className={`${input('logoUrl')} font-data-mono text-xs`}
            {...common('logoUrl', logoBroken ? `${fieldId(index, 'logoUrl')}-broken` : undefined)}
          />
          <BrandLogo url={logoUrl} brand={value.brandName || `L${level}`} size="md" onLoadError={() => setLogoBroken(true)} />
        </div>
        <FieldError id={errors.logoUrl ? `${fieldId(index, 'logoUrl')}-error` : undefined}>{errors.logoUrl}</FieldError>
        {logoBroken && !errors.logoUrl && (
          <p id={`${fieldId(index, 'logoUrl')}-broken`} className="mt-1 flex items-center gap-1 text-[11px] text-status-pending-text">
            <Icon name="broken_image" size={14} />
            Image could not be loaded
          </p>
        )}
      </div>

      <div>
        <Label htmlFor={fieldId(index, 'offerText')} required={!readOnly} count={counter('offerText')}>
          Offer text
        </Label>
        <textarea
          rows={2}
          maxLength={LIMITS.offerText}
          placeholder="e.g. 20% off any product"
          className={`${input('offerText')} resize-y min-h-10`}
          {...common('offerText')}
        />
        <FieldError id={errors.offerText ? `${fieldId(index, 'offerText')}-error` : undefined}>{errors.offerText}</FieldError>
      </div>

      <div>
        <Label htmlFor={fieldId(index, 'offerSubtitle')} count={counter('offerSubtitle')}>
          Offer subtitle
        </Label>
        <input
          type="text"
          autoComplete="off"
          maxLength={LIMITS.offerSubtitle}
          placeholder="e.g. On orders above €25 (optional)"
          className={input('offerSubtitle')}
          {...common('offerSubtitle')}
        />
        <FieldError id={errors.offerSubtitle ? `${fieldId(index, 'offerSubtitle')}-error` : undefined}>{errors.offerSubtitle}</FieldError>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor={fieldId(index, 'couponCode')} count={counter('couponCode')}>
            Coupon code
          </Label>
          <input
            type="text"
            autoComplete="off"
            spellCheck={false}
            maxLength={LIMITS.couponCode}
            placeholder="e.g. HEMA20"
            className={`${input('couponCode')} font-data-mono uppercase`}
            {...common('couponCode', `${fieldId(index, 'couponCode')}-help`)}
            onChange={(e) => onChange(index, 'couponCode', e.target.value.toUpperCase())}
          />
          <FieldError id={errors.couponCode ? `${fieldId(index, 'couponCode')}-error` : undefined}>{errors.couponCode}</FieldError>
          <p id={`${fieldId(index, 'couponCode')}-help`} className="mt-1 text-[11px] text-on-surface-variant">
            One code shared by every user who unlocks this level. Leave empty if codes will be unique per user.
          </p>
        </div>
        <div>
          <Label htmlFor={fieldId(index, 'validForDays')}>Valid for (days)</Label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder={readOnly ? 'No expiry' : 'e.g. 30'}
            className={`${input('validForDays')} font-data-mono`}
            {...common('validForDays')}
          />
          <FieldError id={errors.validForDays ? `${fieldId(index, 'validForDays')}-error` : undefined}>{errors.validForDays}</FieldError>
          {!readOnly && (
            <div className="mt-1.5 flex flex-wrap gap-1" role="group" aria-label={`Level ${level} validity quick picks`}>
              {QUICK_DAYS.map((d) => (
                <button key={d} type="button" aria-pressed={days === String(d)} onClick={() => onChange(index, 'validForDays', String(d))} className={chipClass(days === String(d))}>
                  {d}d
                </button>
              ))}
              <button type="button" aria-pressed={days === ''} onClick={() => onChange(index, 'validForDays', '')} className={chipClass(days === '')}>
                No expiry
              </button>
            </div>
          )}
        </div>
      </div>

      <CouponPreview level={value} scansRequired={tier.scansRequired} />
    </section>
  );
}

function MetaRow({ label, time, actor }) {
  const t = describeTime(time);
  return (
    <div className="flex flex-col">
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant">{label}</dt>
      <dd className="text-xs text-on-surface">
        <span className="font-data-mono">{t.exact}</span>
        {t.relative && <span className="text-on-surface-variant"> · {t.relative}</span>}
        {actor?.name && <span className="block text-on-surface-variant">by {actor.name}</span>}
      </dd>
    </div>
  );
}

/**
 * Add / Edit / View / Duplicate drawer for one coupon package.
 * `couponId` is the package for edit and view, and the source for duplicate.
 * `activeId` is the package app users see now (for the "will be archived" warning).
 */
export default function CouponDrawer({ mode, couponId, tiers, brands = [], activeId, onClose, onEdit, onDelete }) {
  const queryClient = useQueryClient();
  const brandListId = useId();
  const isAdd = mode === 'add';
  const isCreate = isAdd || mode === 'duplicate';
  const readOnly = mode === 'view';

  const couponQuery = useQuery({
    queryKey: ['coupon', couponId],
    queryFn: ({ signal }) => couponsApi.getCouponPackage(couponId, { signal }),
    enabled: !isAdd && couponId != null,
    staleTime: 0, // always edit the latest version
    retry: (count, err) => (err?.status === 0 || err?.status >= 500) && count < 1,
  });
  const coupon = couponQuery.data;
  const count = levelCount(tiers, isAdd ? null : coupon) || 4;

  const [form, setForm] = useState(() => emptyForm(count));
  const initialRef = useRef(form);
  const [loadedId, setLoadedId] = useState(null);
  const [errors, setErrors] = useState({ levels: {} });
  const [generalErrors, setGeneralErrors] = useState([]);
  const [attempted, setAttempted] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Fill the form once the package has loaded (and again if a different one is opened)
  useEffect(() => {
    if (coupon && coupon.id !== loadedId) {
      const next = toForm(coupon, count, { copy: mode === 'duplicate' });
      initialRef.current = next;
      setForm(next);
      setLoadedId(coupon.id);
      setErrors({ levels: {} });
      setGeneralErrors([]);
      setAttempted(false);
    }
  }, [coupon, loadedId, count, mode]);

  // Add opened before the tiers arrived: resize the blank form
  useEffect(() => {
    if (isAdd && form.levels.length !== count && !isDirty(form, initialRef.current)) {
      const next = emptyForm(count);
      initialRef.current = next;
      setForm(next);
    }
  }, [isAdd, count, form]);

  const dirty = !readOnly && isDirty(form, initialRef.current);
  // The active package must stay active until another one replaces it
  const lockedActive = mode === 'edit' && coupon?.status === 'active';
  const becomesActive = form.status === 'active' && !lockedActive && !readOnly;

  const update = (next) => {
    setForm(next);
    setGeneralErrors([]);
    if (attempted) setErrors(validateForm(next));
  };
  const handleLevelChange = (index, field, value) =>
    update({ ...form, levels: form.levels.map((level, i) => (i === index ? { ...level, [field]: value } : level)) });

  const focusField = (id) => {
    const el = id && document.getElementById(id);
    if (!el) return;
    el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    el.focus({ preventScroll: true });
  };

  const save = useMutation({
    mutationFn: (body) => (isCreate ? couponsApi.createCouponPackage(body) : couponsApi.updateCouponPackage(couponId, body)),
    onSuccess: ({ message, coupon: saved }) => {
      toast.success(message || (isCreate ? 'Coupon package created' : 'Coupon package updated'));
      // Making one active archives another, so refresh every cached package
      queryClient.invalidateQueries({ queryKey: ['coupon'] });
      queryClient.setQueryData(['coupon', saved.id], saved);
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      onClose();
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : 'Something went wrong. Please try again.';
      if (err?.status === 400 && err.fieldErrors?.length) {
        const mapped = mapServerErrors(err.fieldErrors);
        setErrors(mapped.errors);
        setGeneralErrors(mapped.general.length ? mapped.general : hasErrors(mapped.errors) ? [] : [message]);
        setAttempted(true);
        requestAnimationFrame(() => focusField(firstErrorId(mapped.errors)));
      } else {
        // 409 (active package rules), 404, 5xx…: the server's own message
        setGeneralErrors([message]);
      }
      toast.error(message);
    },
  });

  const handleSave = () => {
    setAttempted(true);
    const found = validateForm(form);
    setErrors(found);
    setGeneralErrors([]);
    if (hasErrors(found)) {
      focusField(firstErrorId(found));
      return;
    }
    save.mutate(toPayload(form));
  };

  const requestClose = () => {
    if (save.isPending) return;
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };

  const updated = coupon ? describeTime(coupon.updatedAt) : null;
  const title = {
    add: '+ Add New Coupon Package',
    duplicate: `Duplicate Coupon Package (from #${couponId})`,
    view: `Coupon Package (ID: #${couponId})`,
    edit: `Edit Coupon Package (ID: #${couponId})`,
  }[mode];
  const subtitle = isCreate
    ? `Define ${count}-level driver milestone rewards. New packages are saved as Draft unless you choose otherwise.`
    : updated && `Updated: ${updated.exact} • ${count} milestone reward tiers`;

  const loadingCoupon = !isAdd && (couponQuery.isPending || (coupon && coupon.id !== loadedId));
  const loadError = !isAdd && couponQuery.error;
  const activeWarning = activeId
    ? `This replaces the coupons app users see now. The current active package (#${activeId}) will be archived.`
    : 'This replaces the coupons app users see now. The current active package, if any, will be archived.';

  let footer = null;
  if (readOnly) {
    footer = (
      <>
        <span />
        <div className="flex gap-2">
          <Button variant="secondary" size="lg" onClick={onClose}>
            Close
          </Button>
          {coupon && (
            <Button size="lg" icon="edit" onClick={() => onEdit(couponId)}>
              Edit package
            </Button>
          )}
        </div>
      </>
    );
  } else if (!loadError) {
    footer = (
      <>
        {mode === 'edit' ? (
          <span title={lockedActive ? ACTIVE_LOCK_HINT : undefined}>
            <button
              type="button"
              disabled={lockedActive || !coupon || save.isPending}
              onClick={() => onDelete(coupon)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-error hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
            >
              <Icon name="delete_forever" size={18} />
              Delete Coupon Package
            </button>
          </span>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button variant="secondary" size="lg" onClick={requestClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button size="lg" icon={save.isPending ? undefined : 'check'} onClick={handleSave} disabled={save.isPending || loadingCoupon}>
            {save.isPending ? 'Saving…' : isCreate ? 'Save Package' : 'Save Changes'}
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Drawer open title={title} subtitle={subtitle} onRequestClose={requestClose} footer={footer}>
        {loadError ? (
          loadError.status === 404 ? (
            <ErrorState icon="search_off" title="Coupon package not found" description={`Package #${couponId} doesn't exist anymore.`} />
          ) : (
            <ErrorState title="Couldn't load this coupon package" description={loadError.message} onRetry={() => couponQuery.refetch()} />
          )
        ) : loadingCoupon ? (
          <div className="flex flex-col gap-4 animate-pulse" role="status" aria-label="Loading coupon package">
            <div className="h-24 rounded-xl bg-surface-container-low" />
            {Array.from({ length: count }, (_, i) => (
              <div key={i} className="h-64 rounded-xl bg-surface-container-low" />
            ))}
          </div>
        ) : (
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (!readOnly) handleSave();
            }}
            className="flex flex-col gap-4"
          >
            {generalErrors.length > 0 && (
              <div role="alert" className="flex items-start gap-2 rounded-lg border border-status-alert-border bg-status-alert-bg px-3 py-2.5 text-xs text-status-alert-text">
                <Icon name="error" size={16} />
                <ul>
                  {generalErrors.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </div>
            )}

            <section className="bg-surface-container-low rounded-xl border border-outline-variant/30 p-4 flex flex-col gap-3">
              {readOnly ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant">Package name</p>
                    <p className={`text-base font-bold truncate ${coupon?.name?.trim() ? 'text-on-surface' : 'italic text-on-surface-variant'}`}>
                      {packageTitle(coupon)}
                    </p>
                  </div>
                  <PackageStatusBadge status={coupon?.status} />
                </div>
              ) : (
                <>
                  <div>
                    <Label htmlFor="coupon-name" count={`${form.name.length}/${LIMITS.name}`}>
                      Package name
                    </Label>
                    <input
                      id="coupon-name"
                      type="text"
                      autoComplete="off"
                      maxLength={LIMITS.name}
                      placeholder="e.g. Summer 2026 rewards (optional)"
                      value={form.name}
                      onChange={(e) => update({ ...form, name: e.target.value })}
                      aria-invalid={Boolean(errors.name)}
                      aria-describedby={errors.name ? 'coupon-name-error' : undefined}
                      className={`${inputBase} ${errors.name ? inputInvalid : inputEditable} font-semibold`}
                    />
                    <FieldError id={errors.name ? 'coupon-name-error' : undefined}>{errors.name}</FieldError>
                  </div>
                  <StatusPicker
                    value={form.status}
                    lockedActive={lockedActive}
                    error={errors.status}
                    onChange={(status) => update({ ...form, status })}
                  />
                  {becomesActive && (
                    <p role="status" className="flex items-start gap-2 rounded-lg border border-status-pending-border bg-status-pending-bg px-3 py-2 text-xs text-status-pending-text">
                      <Icon name="warning" size={16} className="shrink-0" />
                      {activeWarning}
                    </p>
                  )}
                  {lockedActive && (
                    <p className="flex items-start gap-2 text-xs text-on-surface-variant">
                      <Icon name="info" size={16} className="shrink-0" />
                      This package is what app users see now. To retire it, make another package active.
                    </p>
                  )}
                </>
              )}
              {readOnly && coupon && (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-outline-variant/30">
                  <MetaRow label="Created" time={coupon.createdAt} actor={coupon.createdBy} />
                  <MetaRow label="Last updated" time={coupon.updatedAt} actor={coupon.updatedBy} />
                </dl>
              )}
            </section>

            {form.levels.map((value, index) => (
              <LevelSection
                key={index}
                index={index}
                value={value}
                errors={errors.levels?.[index]}
                tier={getTier(tiers, index + 1)}
                readOnly={readOnly}
                brandListId={brandListId}
                onChange={handleLevelChange}
              />
            ))}
            {!readOnly && (
              <datalist id={brandListId}>
                {brands.map((b) => (
                  <option key={b} value={b} />
                ))}
              </datalist>
            )}
            {/* Lets Enter in a single-line input submit the form */}
            <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
          </form>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirmDiscard}
        title="Discard unsaved changes?"
        description="Your edits to this coupon package will be lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </>
  );
}
